import { InvalidUrlError } from '../../errors/errors';
import { BaseGQLService } from '../../services/BaseGQLService';
import { FilterOperator, SorterSet as GQLSorterSet } from '../../types/gql-types';
import { CursorInfo, PaginationResult } from '../../types/pagination';
import { ModelType } from '../../utils/GQLIdEncoder';
import { TlsHandshakeMapper } from '../mappers/TlsHandshakeMapper';
import { TlsHandshake } from '../models/TlsHandshake';

export const TLS_HANDSHAKE_FRAGMENT = `
id
initiatorIp
initiatorPort
targetIp
targetPort
timestamp
status
serverNameIndication
selectedCiphersuite {
  name
}
serverChosenVersion
`;

export const GET_TLS_HANDSHAKES_QUERY = (variant: ModelType) => `
query (
  $id: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: HandshakesFilter,
  $sorters: [HandshakesSorter],
) {
  node(id: $id) {
    ... on ${variant === ModelType.Report ? ModelType.ReportDone : variant} {
      handshakes(
        first: $first,
        last: $last,
        after: $after,
        before: $before,
        filters: $filters,
        sorters: $sorters,
      ) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            ${TLS_HANDSHAKE_FRAGMENT}
          }
        }
      }
    }
  }
}
`;

export class TlsHandshakesRepository extends BaseGQLService {
  private readonly mapper = new TlsHandshakeMapper();

  async getList(
    variant: ModelType,
    variantId: number,
    cursor: CursorInfo,
    filters: FilterOperator | null,
    sorters: GQLSorterSet[]
  ): Promise<PaginationResult<TlsHandshake>> {
    return this.query(
      GET_TLS_HANDSHAKES_QUERY(variant),
      {
        id: this.gqlIdEncoder.encode(variant, variantId),
        ...this.mapCursorInfo(cursor),
        filters,
        sorters,
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find ${variant} ${variantId}`);
        }
        const { edges, pageInfo, totalCount } = data.node.handshakes;
        return {
          data: edges.map(({ node }: any) => this.mapper.mapGQLModel(node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }
}
